<template>
    <div class="py-10">
        <div v-if="loading" class="mx-auto max-w-screen-2xl text-center">
            <div class="mx-auto max-w-screen-2xl animate-pulse text-center">
                <div class="px-10 lg:px-20">
                    <div class="lg:flex lg:justify-center">
                        <div class="blog-content h-40 rounded bg-gray-200 lg:w-2/5"></div>
                    </div>
                </div>
                <div class="mt-10 mb-10 flex flex-wrap justify-center gap-x-8 px-5 lg:mb-20 lg:px-0">
                    <div class="h-6 w-20 rounded-full bg-gray-200"></div>
                    <div class="mx-2 h-6 w-20 rounded-full bg-gray-200"></div>
                    <div class="h-6 w-20 rounded-full bg-gray-200"></div>
                </div>

                <div
                    class="mx-auto grid grid-cols-2 gap-4 px-10 transition-all duration-500 lg:grid-cols-3 lg:gap-x-4 lg:gap-y-10">
                    <div class="relative h-[230px] md:h-[350px] xl:h-[400px] 2xl:h-[521px]">
                        <div class="h-full w-full rounded bg-gray-200"></div>
                    </div>
                    <div class="relative h-[230px] md:h-[350px] xl:h-[400px] 2xl:h-[521px]">
                        <div class="h-full w-full rounded bg-gray-200"></div>
                    </div>
                    <div class="relative h-[230px] md:h-[350px] xl:h-[400px] 2xl:h-[521px]">
                        <div class="h-full w-full rounded bg-gray-200"></div>
                    </div>
                </div>

                <div
                    class="border-green-550 mx-12 mt-6 flex justify-center gap-x-8 border-t-2 border-b-2 py-2 pt-2.5 lg:gap-x-6">
                    <div class="h-6 w-6 rounded-full bg-gray-200"></div>
                    <div class="mx-2 h-6 w-6 rounded-full bg-gray-200"></div>
                    <div class="h-6 w-6 rounded-full bg-gray-200"></div>
                </div>
            </div>
        </div>
        <div v-else class="mx-auto max-w-screen-2xl text-center">
            <div class="px-10 lg:px-20">
                <div id="blogs-sanity-content" class="lg:flex lg:justify-center">
                    <div class="blog-content lg:w-2/5">
                        <SanityContent :blocks="body" />
                    </div>
                </div>
            </div>
            <div
                id="blogs-filters"
                class="font-monument-extended mt-10 mb-10 flex flex-wrap justify-center gap-x-8 px-5 lg:mb-20 lg:px-0">
                <button
                    @click="updateType('all')"
                    :class="{
                        '!border-green-600 pb-1 !text-black': selectedType === '' || selectedType === 'all',
                    }"
                    class="text-blg-gray-200 !w-max border-b-2 border-transparent text-sm hover:border-b-2 hover:text-green-600 lg:text-lg">
                    {{ snippets['headless.inspiration.everything'] }}
                </button>
                <button
                    @click="updateType('look')"
                    :class="{
                        '!border-green-600 pb-1 !text-black': selectedType == 'look',
                    }"
                    class="text-blg-gray-200 !w-max border-b-2 border-transparent pb-1 text-sm hover:border-b-2 hover:text-green-600 lg:text-lg">
                    {{ snippets['headless.inspiration.looks'] }}
                </button>
                <button
                    @click="updateType('blog')"
                    :class="{
                        '!border-green-600 pb-1 !text-black': selectedType == 'blog',
                    }"
                    class="text-blg-gray-200 !w-max border-b-2 border-transparent pb-1 text-sm hover:border-b-2 hover:text-green-600 lg:text-lg">
                    {{ snippets['headless.inspiration.blog'] }}
                </button>
            </div>

            <div
                class="mx-auto grid grid-cols-2 gap-4 px-10 transition-all duration-500 lg:grid-cols-3 lg:gap-x-4 lg:gap-y-5">
                <div
                    v-for="inspirationSingle in inspiration"
                    :key="inspirationSingle.versionId"
                    class="relative h-[230px] md:h-[350px] xl:h-[400px] 2xl:h-[521px]">
                    <LookElement v-if="inspirationSingle?.type === 'look'" :look="inspirationSingle" />
                    <BlogElement v-if="inspirationSingle?.type === 'blog'" :blog="inspirationSingle" />
                </div>
            </div>
            <div class="span-3" v-if="inspiration.length == 0">
                <h3 class="mx-auto px-4">
                    {{ snippets['headless.blogs.noSearchresults'] }}
                </h3>
            </div>
            <div
                v-if="totalPages > 1"
                class="border-green-550 mx-12 mt-6 flex justify-center gap-x-8 border-t-2 border-b-2 py-2 pt-2.5 lg:gap-x-6">
                <button
                    v-if="totalPages > 1"
                    class="mx-1 bg-green-200 px-2.5 py-2.5 lg:bg-transparent lg:px-4 lg:py-1"
                    :disabled="currentPage === 1"
                    @click="previousPage">
                    <IconArrowRight class="size-3 rotate-180 lg:size-5" />
                </button>
                <button
                    v-for="page in totalPages"
                    :key="page"
                    @click="goToPage(page)"
                    class="mx-1 px-0.5 text-base text-black"
                    :class="{
                        'border-b-2 border-black text-black': currentPage === page,
                    }">
                    {{ page }}
                </button>
                <button
                    v-if="totalPages > 1"
                    class="mx-1 bg-green-200 px-2.5 py-2.5 lg:bg-transparent lg:px-4 lg:py-1"
                    :disabled="currentPage === totalPages"
                    @click="nextPage">
                    <IconArrowRight class="size-3 lg:size-5" />
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    const snippetsStore = useSnippetsStore();
    const snippets = snippetsStore.snippets;

    const props = defineProps({
        body: {
            type: Array,
            required: false,
        },
        gender: {
            type: String,
            required: true,
        },
    });

    const selectedType = ref('');
    const loading = ref(true);
    const currentPage = ref(1);
    const inspiration = ref([]);
    const pagination = ref({});
    const totalPages = ref(1);

    const fetchInspiration = async () => {
        let body = {
            type: selectedType.value,
        };

        if (props.gender !== 'all') {
            body.gender = props.gender;
        }

        const { data, error } = await useFetch('/api/inspiration/getAllInspiration', {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (error.value) {
            return;
        }

        if (data.value) {
            inspiration.value = data.value.inspiration || [];
            pagination.value = data.value.pagination || {};
            totalPages.value = data.value.pagination?.totalPages || 1;
        }
        loading.value = false;
    };

    const updateType = (type: string) => {
        selectedType.value = type;
        currentPage.value = 1;
        fetchInspiration();
    };

    const previousPage = () => {
        if (currentPage.value > 1) {
            currentPage.value -= 1;
            fetchInspiration();
            scrollToElement();
        }
    };

    const nextPage = () => {
        if (currentPage.value < totalPages.value) {
            currentPage.value += 1;
            fetchInspiration();
            scrollToElement();
        }
    };

    const goToPage = (page: number) => {
        currentPage.value = page;
        fetchInspiration();
        scrollToElement();
    };

    const scrollToElement = () => {
        const scrollToElement = document.getElementById('blogs-sanity-content');
        if (scrollToElement) {
            scrollToElement.scrollIntoView({ behavior: 'smooth' });
        }
    };

    watch(
        [selectedType, currentPage],
        () => {
            fetchInspiration();
        },
        { immediate: true },
    );

    const isMobile = computed(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth <= 768;
        }
        return false;
    });

    // Slice description for mobile devices
    const formattedDescription = (description: string) => {
        if (!description) return '';
        if (isMobile.value) {
            const parts = description.split('-');
            return parts.length > 1 ? parts[1].trim() : description;
        }
        return description;
    };

    const formatedSeoUrl = (url: string) => {
        if (!url) return '';
        const segments = url.split('/');
        return `/blog/${segments[segments.length - 1]}`;
    };
</script>

<style>
    .blog-content p {
        margin-top: 38px !important;
    }

    @media (max-width: 500px) {
        .blog-content p {
            margin-top: 12px !important;
        }
    }
</style>
